import { Stack, Typography, useTheme, Box, LinearProgress } from "@mui/material";
import { useEffect } from "react";
import { useRealtimeProductsContext } from "~contexts/RealtimeProducts";
import { useStreamContext } from "~contexts/Stream";
import { useSocketContext } from "~contexts/Socket";
import { EyeIcon } from "~components/Icons";
import { Headline, StreamSearch, BrandSelector } from "~components/atoms";
import { ProductsGrid, SelectedProducts } from "~components/molecules";
import { authFetch } from "~helpers/authFetch";
import { TimesIcon } from "~components/Icons";
import { useToastNotificationContext } from "~contexts/ToastNotification";
import { useProductsContext } from "~contexts/Products";

const RealtimeProduct = () => {
  const theme: any = useTheme();
  const { setToastData } = useToastNotificationContext();
  const { socket } = useSocketContext();
  const { stream, currentStream, playedSeconds, loading } = useStreamContext();
  const { productList, selectedProducts, setSelectedProducts } =
    useRealtimeProductsContext();
  const {
    buttons,
    setButtons,
    setProductList,
    clearAllCache,
    selectCategoryFromCache,
    seedCacheFromStreamButtons,
    loadedProductsFromSheet,
    loadingProducts,
  } = useProductsContext();
  useEffect(() => {
    if (!loading && stream && stream.buttons && currentStream) {
      const streamId = stream.id_stream;
      const currentButtonsId = buttons.length > 0 ? buttons[0]?.id_stream : null;

      if (currentButtonsId === streamId && buttons.length > 0) return;

      clearAllCache();

      setButtons(stream.buttons);

      // Produtos já vêm completos no stream — popular cache e exibir primeira categoria
      seedCacheFromStreamButtons(stream.buttons);

      if (stream.buttons[0]?.id_button) {
        selectCategoryFromCache(stream.buttons[0].id_button);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, loading, currentStream]);

  // Se não há brand selecionada, mostrar mensagem
  if (!currentStream) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          minHeight: "60vh",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.grey[0],
          }}
          variant="h1"
        >
          bem-vindo ao ambiente AzzasLive
        </Typography>
        <Typography
          sx={{
            color: theme.palette.grey[600],
            fontSize: "15px",
            marginBottom: "24px",
          }}
        >
          comece a configurar sua live pelo menu ao lado após selecionar uma marca
        </Typography>
        <BrandSelector invertColors={true} />
      </Box>
    );
  }

  const feedBackMessages = (label: number) => {
    return {
      200: "Linha do tempo modificada com sucesso",
      403: "Modificação na linha do tempo não autorizada",
      400: "Não foi possivel modificar a linha do tempo",
      404: "Linha do tempo não encontrada ou servidor está inativo",
      500: "Erro interno, Não foi possivel modificar a linha do tempo",
    }[label];
  };

  const addProductsTimeline = () => {
    // Se não há brand selecionada, mostrar mensagem
    if (!currentStream) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            minHeight: "60vh",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.grey[0],
            }}
            variant="h1"
          >
            bem-vindo ao ambiente AzzasLive
          </Typography>
          <Typography
            sx={{
              color: theme.palette.grey[600],
              fontSize: "15px",
              marginBottom: "24px",
            }}
          >
            comece a configurar sua live pelo menu ao lado após selecionar uma marca
          </Typography>
          <BrandSelector invertColors={true} />
        </Box>
      );
    }

    const streamId = currentStream.id;
    const reqOptions = {
      path: `${process.env.REACT_APP_BACKEND_URL}/stream/${streamId}/products/timeline`,
      method: "POST",
      data: {
        rerunTimestamp: parseInt(String(playedSeconds * 1000), 10),
        liveTimestamp: Date.now(),
        products: selectedProducts.map((product) => ({
          id_product: product.provider.productId,
        })),
      },
    };

    if (!stream?.video.isRerun) {
      authFetch(reqOptions).then((res) => {
        if (res.status === 200) {
          const updatedSelectedProducts = selectedProducts.map((product) => ({
            ...product,
            isDisabled: true,
          }));

          setSelectedProducts(updatedSelectedProducts);
        } else {
          setToastData(
            { title: "falha ao adicionar", text: feedBackMessages(res.status) },
            "error"
          );
        }
      });
    }
  };

  const onPublish = () => {
    if (stream?.video.isRerun) {
      setToastData(
        {
          title: "ação bloqueada",
          text: "Em modo reprise, o frontend usa apenas a timeline de produto da vez.",
        },
        "error"
      );
      return;
    }

    if (!!selectedProducts.length) {
      // Marca como publicado ANTES de emitir, para o marcador "isDisabled"
      // (= no ar) viajar pelo socket e os outros admins exibirem o card sólido.
      const publishedProducts = selectedProducts.map((product) => ({
        ...product,
        isDisabled: true,
      }));
      addProductsTimeline();
      socket?.emit("setRealTimeProducts", publishedProducts);
      setSelectedProducts(publishedProducts);
      setToastData(
        { title: "produto adicionado", text: "Produto da vez adicionado com sucesso" },
        "success"
      );
    } else {
      setToastData(
        {
          title: "falha ao adicionar",
          text: "Não é possível adicionar um produto da vez em modo reprise! Mudeo-o para ao vivo.",
        },
        "error"
      );
    }
  };

  const onExclude = () => {
    setSelectedProducts([]);
    if (!stream?.video.isRerun) {
      socket?.emit("setRealTimeProducts", []);
    }
  };

  const PRIMARY_BUTTON = {
    icon: <TimesIcon />,
    text: "excluir tudo",
    onAction: () => onExclude(),
  };

  const SECONDARY_BUTTON = {
    icon: <EyeIcon />,
    text: "exibir",
    onAction: () => onPublish(),
  };

  if (!stream || loading) return <>carregando...</>;

  return (
    <Stack>
      <Headline
        id="realtime"
        title="produto da vez"
        primaryButton={PRIMARY_BUTTON}
        secondaryButton={SECONDARY_BUTTON}
      />
      <SelectedProducts />
      <StreamSearch />
      {/* Barra de progresso compartilhada com a aba de produtos */}
      {buttons.length > 0 && (
        <Box sx={{ mt: theme.spacing(4) }}>
          {(() => {
            const totalProducts = buttons.reduce(
              (sum, btn) => sum + (btn.quantity || 0),
              0
            );
            if (!totalProducts) return null;

            const loaded = Math.min(loadedProductsFromSheet, totalProducts);
            const progress = (loaded / totalProducts) * 100;
            const isCompleted = loaded >= totalProducts && !loadingProducts;

            return (
              <>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: theme.palette.grey[400],
                    mb: 0.5,
                  }}
                >
                  {isCompleted
                    ? `carregamento concluído (${totalProducts} items)`
                    : `carregando produtos da planilha (${loaded} de ${totalProducts})`}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Number.isFinite(progress) ? Math.min(progress, 100) : 0}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: isCompleted
                      ? theme.palette.success.light
                      : theme.palette.grey[700],
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: isCompleted
                        ? theme.palette.success.main
                        : theme.palette.warning.main,
                    },
                  }}
                />
              </>
            );
          })()}
        </Box>
      )}
      <Typography sx={{ mt: theme.spacing(4) }}>{productList.length} produtos</Typography>
      <ProductsGrid products={productList} page="realtime" />
    </Stack>
  );
};

export default RealtimeProduct;
